import { useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useLang } from "../../context/LanguageContext"

// ── Beat data ────────────────────────────────────────────────────────────────

const gangsterImg = (team: string, type: string) => `/images/players/${team}/${type}.png`

const FAMILY_KEYS = [
  { nameKey: "demo.beat2.godfather.name",    descKey: "demo.beat2.godfather.desc",    type: "godfather" },
  { nameKey: "demo.beat2.gunman.name",       descKey: "demo.beat2.gunman.desc",       type: "gunman" },
  { nameKey: "demo.beat2.bladeslinger.name", descKey: "demo.beat2.bladeslinger.desc", type: "bladeslinger" },
  { nameKey: "demo.beat2.thug.name",         descKey: "demo.beat2.thug.desc",         type: "thug" },
] as const

const INCOME_KEYS = ["demo.beat4.godfather", "demo.beat4.business", "demo.beat4.monopoly", "demo.beat4.register"] as const

function ObjectiveBeat() {
  const { t } = useLang()
  return (
    <>
      <div className="flex items-center justify-center gap-3">
        {FAMILY_KEYS.map((g) => (
          <img
            key={g.type}
            src={gangsterImg("red", g.type)}
            alt=""
            className="w-14 h-14 object-contain rounded-full border border-[#C9A84C]/30 bg-zinc-900/60"
            draggable={false}
          />
        ))}
      </div>
      <p className="text-zinc-300 text-sm leading-relaxed text-center">{t("demo.beat1.caption")}</p>
    </>
  )
}

function FamilyBeat() {
  const { t } = useLang()
  return (
    <>
      <p className="text-zinc-300 text-sm leading-relaxed text-center">{t("demo.beat2.caption")}</p>
      <ul className="w-full space-y-3">
        {FAMILY_KEYS.map((g) => (
          <li key={g.type} className="text-sm flex gap-3 items-center">
            <img
              src={gangsterImg("red", g.type)}
              alt={t(g.nameKey)}
              className="w-12 h-12 object-contain flex-shrink-0 rounded-full border border-[#C9A84C]/30 bg-zinc-900/60"
              draggable={false}
            />
            <span>
              <span className="text-[#F5AC0E] font-semibold">{t(g.nameKey)} — </span>
              <span className="text-zinc-300 leading-relaxed">{t(g.descKey)}</span>
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

function IncomeBeat() {
  const { t } = useLang()
  return (
    <>
      <p className="text-zinc-300 text-sm leading-relaxed text-center">{t("demo.beat4.caption")}</p>
      <div className="w-full grid grid-cols-2 gap-3">
        {[
          { icon: "/images/players/red/godfather.png", key: INCOME_KEYS[0], round: true },
          { icon: "/images/items/bar.png", key: INCOME_KEYS[1], round: false },
          { icon: "/images/items/casino.png", key: INCOME_KEYS[2], round: false },
          { icon: "/images/items/cash-register.png", key: INCOME_KEYS[3], round: false },
        ].map(({ icon, key, round }) => (
          <div key={key} className="flex items-center gap-2.5 bg-[#0e1f16]/40 border border-[#C9A84C]/20 rounded px-2.5 py-2">
            <img
              src={icon}
              alt=""
              className={`w-9 h-9 object-contain flex-shrink-0 ${round ? "rounded-full border border-[#C9A84C]/30" : ""}`}
              draggable={false}
            />
            <span className="text-xs text-zinc-300 leading-snug">{t(key)}</span>
          </div>
        ))}
      </div>
    </>
  )
}

function CloseBeat() {
  const { t } = useLang()
  return (
    <>
      <div className="flex items-center justify-center">
        <img
          src={gangsterImg("red", "godfather")}
          alt=""
          className="w-20 h-20 object-contain rounded-full border-2"
          style={{ borderColor: "#C9A84C", filter: "drop-shadow(0 0 12px rgba(201,168,76,0.5))" }}
          draggable={false}
        />
      </div>
      <p className="text-zinc-300 text-sm leading-relaxed text-center">{t("demo.beat6.caption")}</p>
    </>
  )
}

// Array-driven so the dot indicator, prev/next boundaries, and total count all fall out
// of its length — trimming or reordering beats never needs a second edit elsewhere.
const BEATS = [ObjectiveBeat, FamilyBeat, IncomeBeat, CloseBeat]

// ── OnboardingDemo ───────────────────────────────────────────────────────────

interface OnboardingDemoProps {
  /** All 4 beats reached — hands off to the live board tour. Does not persist "seen". */
  onFinish: () => void
  /** Skipped at any beat — also hands off to the live board tour (skipping the slides
   *  just gets there faster; it is not "done with onboarding"). Does not persist "seen". */
  onSkip: () => void
}

export function OnboardingDemo({ onFinish, onSkip }: OnboardingDemoProps) {
  const { t } = useLang()
  const [beat, setBeat] = useState(0)
  const isFirst = beat === 0
  const isLast = beat === BEATS.length - 1
  const Beat = BEATS[beat]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={onSkip}>
      <div
        className="bg-[#110806] border border-[#C9A84C]/50 rounded-lg w-full max-w-lg flex flex-col"
        style={{ maxHeight: "88vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — Skip only; visually secondary so it never competes with Next */}
        <div className="flex items-center justify-end px-5 py-3 border-b border-[#C9A84C]/25 flex-shrink-0">
          <button
            onClick={onSkip}
            className="text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1 rounded"
          >
            {t("demo.nav.skip")}
          </button>
        </div>

        {/* Beat dot indicators */}
        <div className="flex gap-1.5 justify-center pt-3 pb-1 flex-shrink-0">
          {BEATS.map((_, i) => (
            <button
              key={i}
              onClick={() => setBeat(i)}
              className="w-2 h-2 rounded-full transition-all duration-200"
              style={{ background: i === beat ? "#C9A84C" : "rgba(201,168,76,0.25)" }}
            />
          ))}
        </div>

        {/* Beat content */}
        <div
          className="px-5 py-4 overflow-y-auto flex-1 flex flex-col items-center justify-center gap-4"
          style={{ minHeight: 300 }}
        >
          <Beat key={beat} />
        </div>

        {/* Navigation footer */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#C9A84C]/20 flex-shrink-0 gap-3">
          <button
            onClick={() => setBeat((b) => b - 1)}
            disabled={isFirst}
            className="flex items-center gap-1.5 text-sm font-serif uppercase tracking-wider transition-colors px-3 py-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ color: "#C9A84C" }}
          >
            <ChevronLeft className="w-4 h-4" />
            {t("demo.nav.back")}
          </button>

          <button
            onClick={isLast ? onFinish : () => setBeat((b) => b + 1)}
            className="flex items-center gap-1.5 text-sm font-serif uppercase tracking-wider transition-opacity px-3 py-1.5 rounded"
            style={{ color: "#F5AC0E" }}
          >
            {isLast ? t("demo.cta.play") : t("demo.nav.next")}
            {!isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}
